import { DownloadCloud, Search, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ParsedReportModal } from "../components/ParsedReportModal";
import { Shell } from "../components/Shell";
import { StatusPill } from "../components/StatusPill";
import { clients as clientsApi, MONITORING_PROVIDERS, reports as reportsApi, type ClientListItem, type CreditReport } from "../lib/api";

// The table used to print raw column values: a truncated client UUID, the raw
// ISO pulled_at string, a snake_case provider id and a snake_case parser error
// code. Staff could not tell whose report a row was or what went wrong.
const PROVIDER_LABELS: Record<string, string> = {
  ...Object.fromEntries(MONITORING_PROVIDERS.map((p) => [p.value, p.label])),
  credit_karma: "Credit Karma",
  isoftpull: "iSoftpull",
  manual_upload: "Manual upload",
  pdf_upload: "PDF upload",
};

/** pulled_at is a DATE column; render it short, never a raw ISO timestamp. */
function formatPulled(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00` : v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
}

/** Parser error codes are internal. Show a sentence; keep the code in title. */
const PARSE_ERROR_TEXT: Record<string, string> = {
  scores_not_auto_extracted: "Scores could not be read automatically",
  no_tradelines_found: "No accounts found in the report",
  locked_report: "Report is locked by the provider",
  single_bureau_plan: "Provider plan only covers one bureau",
};
function humanizeParseError(v: string | null | undefined): string {
  if (!v) return "—";
  return PARSE_ERROR_TEXT[v] || v.replace(/_/g, " ");
}

export default function ReportsPage() {
  const [list, setList] = useState<CreditReport[]>([]);
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [pickClient, setPickClient] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [viewReportId, setViewReportId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Delete one imported report. Permanent — the captured credit file and its
  // analysis go with it — so it asks first, naming whose report it is.
  async function deleteReport(r: CreditReport) {
    const who = r.client_name || `client ${r.client_id.slice(0, 8)}`;
    if (!window.confirm(
      `Delete this ${PROVIDER_LABELS[r.provider] || r.provider} report for ${who}?\n\nThis permanently removes the captured report and its analysis. It cannot be undone.`,
    )) return;
    try {
      await reportsApi.remove(r.id);
      setList((prev) => prev.filter((x) => x.id !== r.id));
      setInfo(`Report deleted for ${who}.`);
    } catch (e: any) { setError(e?.message || "Could not delete the report."); }
  }

  async function load() {
    try { setList(await reportsApi.list()); } catch {}
    try {
      const r = await clientsApi.list({ limit: 200 });
      setClients(r.items);
    } catch {}
  }
  useEffect(() => { load(); }, []);

  async function upload(file: File) {
    if (!pickClient) { setError("Pick a client first"); return; }
    setUploading(true); setError(null); setInfo(null);
    try {
      await reportsApi.upload(pickClient, file);
      await load();
    } catch (e: any) { setError(e?.message); }
    finally { setUploading(false); }
  }

  async function importFromCredentials() {
    if (!pickClient) { setError("Pick a client first"); return; }
    setImporting(true); setError(null); setInfo(null);
    try {
      const result: any = await reportsApi.importFromCredentials(pickClient);
      const report = result?.report ?? result;
      const scores = result?.scores ?? null;
      if (report?.parse_status === "failed") {
        const err = (report?.parse_error || "").toLowerCase();
        if (err.includes("rate limit") || err.includes("429")) {
          setError("Cloudflare browser rate-limit hit — wait a few minutes and try again. (Free tier; upgrade Cloudflare Workers Paid to remove this limit.)");
        } else if (err.includes("invalid email or password") || err.includes("auth_failed")) {
          setError("MyFreeScoreNow rejected the credentials. Verify the email and password on the client's record.");
        } else if (err.includes("timeout")) {
          setError("Import timed out. MFSN may be slow right now — try again in a few minutes.");
        } else {
          setError(`Import failed: ${report?.parse_error || "unknown error"}`);
        }
      } else if (report?.parse_status === "needs_manual") {
        setInfo("Imported — raw report saved. Score auto-extraction didn't run (the analyzer will still use the HTML).");
      } else {
        const bits: string[] = [];
        if (scores?.equifax)    bits.push(`Equifax ${scores.equifax}`);
        if (scores?.experian)   bits.push(`Experian ${scores.experian}`);
        if (scores?.transunion) bits.push(`TransUnion ${scores.transunion}`);
        setInfo(bits.length ? `Imported. Scores: ${bits.join(" · ")}` : "Imported successfully.");
      }
      await load();
    } catch (e: any) {
      setError(e?.message || "Import failed");
    } finally {
      setImporting(false);
    }
  }

  const noClient = !pickClient;
  const importTooltip = noClient
    ? "Pick a client before importing"
    : "Pulls the credit report using the client's saved MyFreeScoreNow or SmartCredit credentials";

  return (
    <Shell title="Credit Reports" subtitle="Imports" actions={<span />}>
      <div className="panel">
        <div className="panel-header">
          <div><h2>Import</h2></div>
        </div>
        <div className="form-row">
          <select
            value={pickClient}
            onChange={(e) => setPickClient(e.target.value)}
            aria-label="Filter reports by client"
          >
            <option value="">Select a client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.legal_name}</option>
            ))}
          </select>
          <button
            className="primary-button"
            onClick={importFromCredentials}
            disabled={noClient || importing || uploading}
            title={importTooltip}
          >
            <DownloadCloud size={16} />{" "}
            {importing ? "Importing… this can take up to 90 seconds" : "Import from Credentials"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
          />
          <button
            className="primary-button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || importing}
          >
            <Upload size={16} /> {uploading ? "Uploading…" : "Upload PDF"}
          </button>
        </div>
        {error && <p className="error-text">{error}</p>}
        {info && <p className="muted small">{info}</p>}
      </div>

      <div className="panel">
        <div className="panel-header">
          <div><h2>Imported reports</h2></div>
        </div>
        <div className="table-list">
          <div className="table-row" style={{ fontWeight: 600, fontSize: 12, color: "var(--ne-muted)", textTransform: "uppercase", letterSpacing: 0.4, gridTemplateColumns: "64px minmax(120px, 1fr) minmax(104px, auto) 100px 130px minmax(112px, auto) minmax(60px, 1fr) 84px" }}>
            <span>Report ID</span>
            <span>Client</span>
            <span>Provider</span>
            <span>Pulled</span>
            <span>Imported</span>
            <span>Status</span>
            <span>Error</span>
            <span>Actions</span>
          </div>
          {list.length === 0 && <p className="muted small">No reports yet.</p>}
          {list.map((r) => {
            const importedFull = r.imported_at ? new Date(r.imported_at) : null;
            const importedLabel = importedFull
              ? `${importedFull.toLocaleDateString()} ${importedFull.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
              : "—";
            return (
              <div className="table-row" key={r.id} style={{ gridTemplateColumns: "64px minmax(120px, 1fr) minmax(104px, auto) 100px 130px minmax(112px, auto) minmax(60px, 1fr) 84px" }}>
                <span>{r.id.slice(0, 8)}</span>
                <strong title={r.client_name || r.client_id}>{r.client_name || `${r.client_id.slice(0, 8)}…`}</strong>
                <span>{PROVIDER_LABELS[r.provider] || r.provider.replace(/_/g, " ")}</span>
                <span>{formatPulled(r.pulled_at)}</span>
                <span title={importedFull?.toString()}>{importedLabel}</span>
                <StatusPill status={r.parse_status} kind="report" />
                <span title={r.parse_error || undefined}>{humanizeParseError(r.parse_error)}</span>
                <span style={{ display: "inline-flex", gap: 6 }}>
                  <button
                    className="icon-button"
                    title="View report"
                    aria-label="View report"
                    data-testid={`view-parsed-${r.id}`}
                    onClick={() => setViewReportId(r.id)}
                    style={{ height: 28, padding: "0 8px" }}
                  >
                    <Search size={14} />
                  </button>
                  <button
                    className="icon-button danger"
                    title="Delete this report"
                    aria-label={`Delete report for ${r.client_name || r.client_id.slice(0, 8)}`}
                    data-testid={`delete-report-${r.id}`}
                    onClick={() => deleteReport(r)}
                    style={{ height: 28, padding: "0 8px" }}
                  >
                    <Trash2 size={14} />
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <ParsedReportModal
        open={!!viewReportId}
        reportId={viewReportId}
        onClose={() => setViewReportId(null)}
      />
    </Shell>
  );
}
